import type { Engine } from '../types';
import { buildUrl, joinQuery } from '../url';
import { LANGUAGES } from './github';

export const stackoverflow: Engine = {
  id: 'stackoverflow',
  name: 'Stack Overflow',
  bang: 'so',
  aliases: ['stackoverflow'],
  homepage: 'https://stackoverflow.com',
  category: 'Developer',
  description:
    'Questions and answers, filtered by tag, score and accepted answers.',
  params: [
    {
      id: 'tag',
      type: 'text',
      prefix: '@',
      pattern: /^[a-z0-9+#-][a-z0-9+#-]{0,34}$/,
      label: 'Tag',
      group: 'Scope',
      placeholder: 'rust',
      help: '@rust searches questions tagged [rust].',
    },
    {
      id: 'questions',
      type: 'flag',
      token: 'q',
      label: 'Questions only',
      group: 'Filters',
    },
    {
      id: 'accepted',
      type: 'flag',
      token: 'accepted',
      label: 'Has an accepted answer',
      group: 'Filters',
    },
    {
      id: 'answered',
      type: 'flag',
      token: 'answered',
      label: 'Has an upvoted answer',
      group: 'Filters',
    },
    {
      id: 'code',
      type: 'flag',
      token: 'code',
      label: 'Contains code',
      group: 'Filters',
    },
    {
      id: 'score',
      type: 'number',
      prefix: '*',
      label: 'Min score',
      group: 'Filters',
      placeholder: '5',
    },
    {
      id: 'years',
      type: 'range',
      prefix: 'y',
      label: 'Asked in',
      group: 'Filters',
      help: 'y2024- = 2024 or later.',
    },
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        { value: 'votes', token: 'votes', label: 'Most votes' },
        { value: 'newest', token: 'newest', label: 'Newest' },
        { value: 'active', token: 'active', label: 'Recent activity' },
      ],
    },
  ],
  build({ query, values }) {
    const years = typeof values.years === 'object' ? values.years : undefined;
    return buildUrl('https://stackoverflow.com/search', {
      q: joinQuery(
        typeof values.tag === 'string' && `[${values.tag}]`,
        query,
        values.questions === true && 'is:question',
        values.accepted === true && 'hasaccepted:yes',
        values.answered === true && 'isanswered:yes',
        values.code === true && 'hascode:yes',
        typeof values.score === 'number' && `score:${values.score}..`,
        years && `created:${years.min ?? ''}..${years.max ?? ''}`
      ),
      tab: values.sort as string | undefined,
    });
  },
};

export const hackernews: Engine = {
  id: 'hackernews',
  name: 'Hacker News',
  bang: 'hn',
  aliases: ['hackernews'],
  homepage: 'https://news.ycombinator.com',
  category: 'Developer',
  description: 'Stories and comments via Algolia’s HN search.',
  params: [
    {
      id: 'type',
      type: 'choice',
      label: 'Type',
      group: 'Type',
      options: [
        { value: 'story', token: 'stories', label: 'Stories' },
        { value: 'comment', token: 'comments', label: 'Comments' },
        { value: 'ask_hn', token: 'ask', label: 'Ask HN' },
        { value: 'show_hn', token: 'show', label: 'Show HN' },
        { value: 'job', token: 'jobs', label: 'Jobs' },
      ],
    },
    {
      id: 'range',
      type: 'choice',
      label: 'Time range',
      group: 'Filters',
      options: [
        { value: 'last24h', token: 'd', label: 'Past 24 hours' },
        { value: 'pastWeek', token: 'w', label: 'Past week' },
        { value: 'pastMonth', token: 'm', label: 'Past month' },
        { value: 'pastYear', token: 'y', label: 'Past year' },
      ],
    },
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [{ value: 'byDate', token: 'newest', label: 'Newest' }],
    },
  ],
  build({ query, values }) {
    return buildUrl('https://hn.algolia.com/', {
      query,
      type: (values.type as string | undefined) ?? 'all',
      dateRange: (values.range as string | undefined) ?? 'all',
      sort: (values.sort as string | undefined) ?? 'byPopularity',
    });
  },
};

export const scholar: Engine = {
  id: 'scholar',
  name: 'Google Scholar',
  bang: 'scholar',
  aliases: ['gs'],
  homepage: 'https://scholar.google.com',
  category: 'Research',
  description:
    'Papers by year range, with review-only and recent-first options.',
  params: [
    {
      id: 'years',
      when: { param: 'recent', unset: true },
      whenLabel:
        'can’t be combined with Newest first (Google sorts only the past year by date)',
      type: 'range',
      prefix: 'y',
      label: 'Years',
      group: 'Filters',
      help: 'y2020-2024, y2022-.',
    },
    {
      id: 'reviews',
      type: 'flag',
      token: 'reviews',
      label: 'Review articles only',
      group: 'Filters',
    },
    {
      id: 'nocite',
      type: 'flag',
      token: 'nocite',
      label: 'Hide citation-only results',
      group: 'Filters',
    },
    {
      id: 'recent',
      when: { param: 'years', unset: true },
      whenLabel: 'can’t be combined with a year range',
      type: 'flag',
      token: 'newest',
      label: 'Newest first',
      group: 'Sort',
      help: 'Google only sorts the past year by date.',
    },
  ],
  build({ query, values }) {
    const years = typeof values.years === 'object' ? values.years : undefined;
    return buildUrl('https://scholar.google.com/scholar', {
      q: query,
      as_ylo: years?.min,
      as_yhi: years?.max,
      as_rr: values.reviews === true && '1',
      as_vis: values.nocite === true && '1',
      scisbd: values.recent === true && '2',
    });
  },
};

export const arxiv: Engine = {
  id: 'arxiv',
  name: 'arXiv',
  bang: 'arxiv',
  aliases: ['arx'],
  homepage: 'https://arxiv.org',
  category: 'Research',
  description: 'Preprints by title, author or abstract.',
  params: [
    {
      id: 'field',
      type: 'choice',
      label: 'Search in',
      group: 'Scope',
      options: [
        { value: 'title', token: 'title', label: 'Title' },
        { value: 'author', token: 'author', label: 'Author' },
        { value: 'abstract', token: 'abstract', label: 'Abstract' },
        { value: 'full_text', token: 'fulltext', label: 'Full text' },
      ],
    },
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        { value: '-announced_date_first', token: 'newest', label: 'Newest' },
        { value: 'announced_date_first', token: 'oldest', label: 'Oldest' },
      ],
    },
  ],
  build({ query, values }) {
    return buildUrl('https://arxiv.org/search/', {
      query,
      searchtype: (values.field as string | undefined) ?? 'all',
      order: values.sort as string | undefined,
      size: '50',
    });
  },
};

export const npm: Engine = {
  id: 'npm',
  name: 'npm',
  bang: 'npm',
  aliases: [],
  homepage: 'https://www.npmjs.com',
  category: 'Developer',
  description: 'JavaScript packages.',
  params: [
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        {
          value: 'downloads_weekly',
          token: 'downloads',
          label: 'Weekly downloads',
        },
        {
          value: 'dependent_count',
          token: 'dependents',
          label: 'Most dependents',
        },
        { value: 'published_at', token: 'newest', label: 'Recently published' },
      ],
    },
  ],
  build({ query, values }) {
    return buildUrl('https://www.npmjs.com/search', {
      q: query,
      sortBy: values.sort as string | undefined,
    });
  },
};

export const pypi: Engine = {
  id: 'pypi',
  name: 'PyPI',
  bang: 'pypi',
  aliases: [],
  homepage: 'https://pypi.org',
  category: 'Developer',
  description: 'Python packages.',
  params: [
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        { value: '-created', token: 'newest', label: 'Recently updated' },
      ],
    },
  ],
  build({ query, values }) {
    return buildUrl('https://pypi.org/search/', {
      q: query,
      o: values.sort as string | undefined,
    });
  },
};

export const crates: Engine = {
  id: 'crates',
  name: 'crates.io',
  bang: 'crates',
  aliases: ['crate', 'cr'],
  homepage: 'https://crates.io',
  category: 'Developer',
  description: 'Rust crates.',
  params: [
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        { value: 'downloads', token: 'downloads', label: 'All-time downloads' },
        {
          value: 'recent-downloads',
          token: 'recent',
          label: 'Recent downloads',
        },
        {
          value: 'recent-updates',
          token: 'updated',
          label: 'Recently updated',
        },
        { value: 'new', token: 'newest', label: 'Newly added' },
      ],
    },
  ],
  build({ query, values }) {
    return buildUrl('https://crates.io/search', {
      q: query,
      sort: values.sort as string | undefined,
    });
  },
};

export const grepapp: Engine = {
  id: 'grepapp',
  name: 'grep.app',
  bang: 'grep',
  aliases: ['grepapp'],
  homepage: 'https://grep.app',
  category: 'Developer',
  description: 'Regex code search across a million GitHub repos, no sign-in.',
  params: [
    {
      id: 'regex',
      type: 'flag',
      token: 're',
      label: 'Regular expression',
      group: 'Match',
    },
    {
      id: 'case',
      type: 'flag',
      token: 'case',
      label: 'Match case',
      group: 'Match',
    },
    {
      id: 'words',
      when: { param: 'regex', unset: true },
      whenLabel: 'doesn’t apply to regular expressions',
      type: 'flag',
      token: 'word',
      label: 'Whole words',
      group: 'Match',
    },
    {
      id: 'language',
      type: 'choice',
      label: 'Language',
      group: 'Filters',
      options: LANGUAGES.map(([token, name]) => ({
        value: name,
        token,
        label: name,
      })),
    },
    {
      id: 'repo',
      type: 'text',
      prefix: '@',
      pattern: /^[a-z0-9_-]+\/[a-z0-9_-]+$/,
      label: 'Repository',
      group: 'Filters',
      placeholder: 'vercel/swr',
      help: '@owner/name. Repos with dots in the name need the form.',
    },
  ],
  build({ query, values }) {
    return buildUrl('https://grep.app/search', {
      q: query,
      regexp: values.regex === true && 'true',
      case: values.case === true && 'true',
      words: values.words === true && 'true',
      'f.lang': values.language as string | undefined,
      'f.repo': values.repo as string | undefined,
    });
  },
};
